import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  BookOpen,
  MapPin,
  Upload,
  RotateCcw,
  ImagePlus,
  Loader2,
  Calculator,
  Hotel,
  DoorOpen,
  DoorClosed,
  Lock,
  Unlock,
  BookOpenCheck,
  CheckCircle2,
  ArrowUpDown,
  SlidersHorizontal,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AttendanceLoaders, saveAttendanceData } from "@/components/AttendanceExcelButton";
import { CourseProgressChips } from "@/components/CourseProgressChips";
import { adminApi } from "@/lib/auth";
import InstructorAssignment from "@/components/InstructorAssignment";
import { AgentCheckBar } from "@/components/AgentCheckBar";
import {
  PpaaCourseFields, PPAA_MAX_LINES, ppaaSyllabusItems, ppaaSyllabusLines,
} from "@/components/PpaaCourseFields";
import { logError, logWarn } from "@/lib/log";

const emptyForm = {
  client_name: "",
  address: "",
  city: "",
  region_id: undefined,
  course_type: "inicial",
  days: 1,
  date1: "",
  date2: "",
  date3: "",
  time_start_day1: "",
  time_end_day1: "",
  time_start_day2: "",
  time_end_day2: "",
  time_start_day3: "",
  time_end_day3: "",
  instructor_id: undefined,
  fundae: false,
  defibrillator: "",
  pernocta: false,
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  bsafe_contact_id: "",
  description: "",
  active: true,
  exam_question_count: 15,
  exam_pass_score: 50,
  ppaa_course_name: "Primeros Auxilios",
  ppaa_syllabus: "",
};

const COURSE_TYPE_LABEL = { inicial: "Inicial", reciclaje: "Reciclaje", ppaa: "PPAA" };
const COURSE_TYPE_BADGE = {
  inicial: "bg-sky-50 text-sky-800 border-sky-200",
  reciclaje: "bg-amber-50 text-amber-800 border-amber-200",
  ppaa: "bg-rose-50 text-rose-800 border-rose-200",
};

const DEFIBRILLATOR_BRANDS = ["PHILIPS", "MINDRAY", "ZOLL", "IPAD"];

// -------- Listado: orden y filtros --------
const emptyFilters = {
  client: "",
  month: "",
  instructor: "",
  region: "",
  type: "",
  status: "",
  fundae: "",
  billed: "",
};

const SORT_OPTIONS = [
  { value: "date_asc", label: "Fecha ↑ (más próxima)" },
  { value: "date_desc", label: "Fecha ↓ (más lejana)" },
  { value: "alpha_asc", label: "Cliente A → Z" },
  { value: "alpha_desc", label: "Cliente Z → A" },
];

const MONTHS = [
  ["01", "Enero"], ["02", "Febrero"], ["03", "Marzo"], ["04", "Abril"],
  ["05", "Mayo"], ["06", "Junio"], ["07", "Julio"], ["08", "Agosto"],
  ["09", "Septiembre"], ["10", "Octubre"], ["11", "Noviembre"], ["12", "Diciembre"],
];

// "2026-10-08" → "08/10/2026"
const formatDateEs = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

// Todas las fechas del curso (día 1, día 2 y día 3 en cursos de Murcia)
const courseDatesLabel = (c) => {
  const days = Number(c?.days) || 1;
  const dates = [c?.date1];
  if (days >= 2 && c?.date2 && c.date2 !== c.date1) dates.push(c.date2);
  if (days >= 3 && c?.date3 && c.date3 !== c.date1) dates.push(c.date3);
  return dates.filter(Boolean).map(formatDateEs).join(" · ");
};

// "Nombre + primer apellido" del instructor asignado al curso
const instructorShortName = (course, instructors) => {
  const ins = instructors.find((i) => i.id === course?.instructor_id);
  if (ins) {
    const first = String(ins.surname || "").trim().split(/\s+/)[0] || "";
    return `${String(ins.name || "").trim()} ${first}`.trim();
  }
  const parts = String(course?.instructor_name || "").trim().split(/\s+/);
  return parts.slice(0, 2).join(" ");
};


// Helper: convert a File to a data URL (base64). Module-level so React's
// hook-rule linter doesn't complain about recreating it on each render.
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export default function CoursesManager() {
  const [courses, setCourses] = useState([]);
  const [regions, setRegions] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [bsafeContacts, setBsafeContacts] = useState([]);
  const [bsafeDialogOpen, setBsafeDialogOpen] = useState(false);
  const [bsafeForm, setBsafeForm] = useState({
    id: null, name: "", surname: "", email: "", phone: "",
  });
  const [bsafeSaving, setBsafeSaving] = useState(false);
  const [adminGroup, setAdminGroup] = useState("desfiemer");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const editingCourse = useMemo(
    () => (editingId ? courses.find((c) => c.id === editingId) : null),
    [editingId, courses]
  );
  const [resettingQuestions, setResettingQuestions] = useState(false);

  // Toggle Asistencia + Examen desde el panel del gestor
  const [toggling, setToggling] = useState(null); // { id, kind: 'attendance'|'exam' } | null
  const toggleCourseField = useCallback(async (course, kind) => {
    const id = course.id;
    setToggling({ id, kind });
    try {
      const isAttendance = kind === "attendance";
      const url = isAttendance
        ? `/admin/courses/${id}/attendance-toggle`
        : `/admin/courses/${id}/exam-toggle`;
      const currentOpen = isAttendance
        ? course.attendance_open !== false
        : !!course.exam_unlocked;
      const body = isAttendance
        ? { open: !currentOpen }
        : { unlocked: !currentOpen };
      await adminApi.post(url, body);
      setCourses((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                ...(isAttendance
                  ? { attendance_open: !currentOpen }
                  : { exam_unlocked: !currentOpen }),
              }
            : c
        )
      );
      toast.success(
        isAttendance
          ? !currentOpen
            ? "Asistencia ABIERTA."
            : "Asistencia CERRADA."
          : !currentOpen
            ? "Examen INICIADO."
            : "Examen FINALIZADO."
      );
    } catch (e) {
      logError("No se pudo cambiar el estado del curso", e);
      toast.error(e?.response?.data?.detail || "Error inesperado.");
    } finally {
      setToggling(null);
    }
  }, []);

  // Corrector del examen (mismo dialog que el instructor)
  const [correctorCourse, setCorrectorCourse] = useState(null);
  const [correctorQuestions, setCorrectorQuestions] = useState([]);
  const [correctorLoading, setCorrectorLoading] = useState(false);
  const openCorrector = useCallback(async (course) => {
    setCorrectorCourse(course);
    setCorrectorLoading(true);
    try {
      const r = await adminApi.get(
        `/admin/courses/${course.id}/exam-questions`
      );
      setCorrectorQuestions(r.data?.questions || []);
    } catch (e) {
      logError("No se pudieron cargar las preguntas", e);
      toast.error("No se pudieron cargar las preguntas.");
      setCorrectorCourse(null);
    } finally {
      setCorrectorLoading(false);
    }
  }, []);

  const handleResetExamQuestions = async () => {
    if (!editingId) return;
    if (
      !window.confirm(
        "¿Resetear las preguntas del examen?\n\n" +
        "El próximo alumno que inicie un examen sorteará un nuevo conjunto " +
        "de preguntas del banco. Si ya hay intentos hechos, las correcciones " +
        "anteriores no se ven afectadas."
      )
    ) {
      return;
    }
    setResettingQuestions(true);
    try {
      await adminApi.post(`/courses/${editingId}/regenerate-questions`);
      toast.success("Preguntas reseteadas. Se sortearán al iniciar el próximo examen.");
      await fetchAll();
    } catch (e) {
      logError("Error reseteando preguntas", e);
      toast.error(e.response?.data?.detail || "No se pudo resetear las preguntas.");
    } finally {
      setResettingQuestions(false);
    }
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pinRegenTarget, setPinRegenTarget] = useState(null);
  const [pinRegenLoading, setPinRegenLoading] = useState(false);
  const [pinRegenResult, setPinRegenResult] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [studentsModal, setStudentsModal] = useState(null);
  const [studentsModalData, setStudentsModalData] = useState({ loading: false, attendances: [], pre: [] });
  // --- OCR de pantallazo (carga datos de un correo/whatsapp) ---
  const [parsingScreenshot, setParsingScreenshot] = useState(false);
  // --- Estimacion de coste del curso (km + horas) ---

  // -------- Listado: filtros y orden --------
  // Por defecto sólo se muestran cursos "en curso o futuros" (fecha del día 1
  // ≥ hoy en Europe/Madrid). El usuario puede pulsar "Ver pasados" para
  // alternar a los antiguos. Además existe un selector por CCAA limitado a
  // las regiones donde DESFIEMER opera (vienen ya en `regions`).
  const [showPast, setShowPast] = useState(false);
  // Ordenación: por defecto fecha ascendente (curso más próximo primero).
  const [sortMode, setSortMode] = useState("date_asc");
  // Filtros combinables del listado ("" = sin filtro).
  const [filters, setFilters] = useState(emptyFilters);
  // IDs de cursos que ya tienen factura (filtro Facturado / Sin facturar)
  const [invoicedCourseIds, setInvoicedCourseIds] = useState(() => new Set());
  // Auto-detección de CCAA a partir de la dirección + ciudad (Nominatim)
  const [ccaaAutoDetecting, setCcaaAutoDetecting] = useState(false);
  const [ccaaAutoHint, setCcaaAutoHint] = useState(null);

  // Fetch full data when a student modal opens
  useEffect(() => {
    if (!studentsModal) return;
    let cancelled = false;
    (async () => {
      setStudentsModalData({ loading: true, attendances: [], pre: [] });
      try {
        const [aRes, sRes] = await Promise.all([
          adminApi.get(`/attendance?course_id=${studentsModal.id}`),
          adminApi.get(`/courses/${studentsModal.id}/students`),
        ]);
        if (!cancelled) {
          setStudentsModalData({
            loading: false,
            attendances: aRes.data || [],
            pre: sRes.data || [],
          });
        }
      } catch (e) {
        if (!cancelled) {
          setStudentsModalData({ loading: false, attendances: [], pre: [] });
          toast.error("No se pudo cargar el listado de alumnos.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // adminApi/toast son imports estables; setStudentsModalData lo es por
    // garantia de React. La unica dep variable es studentsModal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsModal]);

  const handleUploadExcel = async (course, file) => {
    if (!file) return;
    setUploadingFor(course.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.post(
        `/courses/${course.id}/students/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `Importados ${res.data?.imported ?? 0} alumnos para ${
          course.client_name || course.name
        }.`
      );
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al importar el Excel.";
      toast.error(typeof msg === "string" ? msg : "Error al importar");
    } finally {
      setUploadingFor(null);
    }
  };

  // 2026-06-10: Solo gestores DESFIEMER. Conservamos isIssefe/adminGroup
  // para no romper otras ramas legacy si quedaran.
  const isIssefe = adminGroup === "issefe";  // eslint-disable-line no-unused-vars
  const createLabel = "Crear curso";

  // Total scheduled hours (client-side mirror of backend logic)
  const totalScheduledHours = (() => {
    const diff = (s, e) => {
      if (!s || !e) return null;
      const [sh, sm] = s.split(":").map(Number);
      const [eh, em] = e.split(":").map(Number);
      const mins = eh * 60 + em - (sh * 60 + sm);
      return mins > 0 ? mins / 60 : null;
    };
    const h1 = diff(form.time_start_day1, form.time_end_day1);
    if (h1 === null) return null;
    let total = h1;
    const days = parseInt(form.days, 10);
    if (days >= 2) {
      const h2 = diff(form.time_start_day2, form.time_end_day2);
      if (h2 === null) return null;
      total += h2;
      if (days >= 3) {
        const h3 = diff(form.time_start_day3, form.time_end_day3);
        if (h3 === null) return null;
        total += h3;
      }
    } else if (days === 1 && form.course_type === "inicial") {
      // 1-day initial course is split in 2 sessions (mañana + tarde)
      const h2 = diff(form.time_start_day2, form.time_end_day2);
      if (h2 === null) return null;
      total += h2;
    }
    return Math.round(total * 100) / 100;
  })();

  // Validación blanda de coherencia entre las dos sesiones / días.
  // Devuelve string con el aviso, o null si todo está OK.
  // No bloquea el guardado — solo muestra una advertencia visible.
  const sessionWarning = useMemo(() => {
    const {
      time_start_day1: s1,
      time_end_day1: e1,
      time_start_day2: s2,
      time_end_day2: e2,
      time_start_day3: s3,
      time_end_day3: e3,
      date1,
      date2,
      date3,
      days,
      course_type,
    } = form;
    const showSession2 = days >= 2 || (days === 1 && course_type === "inicial");

    // 1. Sesión 1 invertida
    if (s1 && e1 && s1 >= e1) {
      return `La hora de fin (${e1}) es anterior o igual a la de inicio (${s1}).`;
    }
    if (showSession2) {
      // 2. Sesión 2 invertida
      if (s2 && e2 && s2 >= e2) {
        return `Sesión 2: la hora de fin (${e2}) es anterior o igual a la de inicio (${s2}).`;
      }
      // 3. Solape entre sesiones del mismo día (solo cuando es 1 día con 2 sesiones)
      if (days === 1 && course_type === "inicial" && e1 && s2 && e1 > s2) {
        return `Las dos sesiones se solapan: la sesión 1 termina a las ${e1} pero la sesión 2 empieza a las ${s2}.`;
      }
    }

    // 4. Fecha 2 anterior o igual a Fecha 1 (cursos de 2 o 3 días)
    if (days >= 2 && date1 && date2 && date2 <= date1) {
      return `La fecha del día 2 (${date2}) debe ser posterior a la del día 1 (${date1}).`;
    }

    // 4b. Cursos de 3 días (MURCIA): sesión 3 coherente
    if (days >= 3) {
      if (s3 && e3 && s3 >= e3) {
        return `Sesión 3: la hora de fin (${e3}) es anterior o igual a la de inicio (${s3}).`;
      }
      if (date3 && date2 && date3 <= date2) {
        return `La fecha del día 3 (${date3}) debe ser posterior a la del día 2 (${date2}).`;
      }
    }

    // 5. Curso en pasado (avisamos solo, sigue siendo válido editar)
    if (date1) {
      const today = new Date().toISOString().slice(0, 10);
      if (date1 < today) {
        return `La fecha del curso (${date1}) está en el pasado. Comprueba que es correcta.`;
      }
    }

    return null;
  }, [form]);

  const [hoursWarning, setHoursWarning] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, i, me, inv, bs] = await Promise.all([
        adminApi.get("/courses"),
        adminApi.get("/regions"),
        adminApi.get("/instructors"),
        adminApi.get("/auth/me-admin"),
        adminApi.get("/admin/invoicing/invoices").catch(() => ({ data: [] })),
        adminApi.get("/bsafe-contacts").catch(() => ({ data: [] })),
      ]);
      setBsafeContacts(bs.data || []);
      setCourses(c.data || []);
      setRegions(
        [...(r.data || [])].sort((a, b) =>
          String(a?.name || "").localeCompare(
            String(b?.name || ""), "es", { sensitivity: "base" }
          )
        )
      );
      setInstructors(
        [...(i.data || [])].sort((a, b) =>
          `${a?.name || ""} ${a?.surname || ""}`.localeCompare(
            `${b?.name || ""} ${b?.surname || ""}`,
            "es", { sensitivity: "base" }
          )
        )
      );
      setAdminGroup(me.data?.group || "desfiemer");
      setInvoicedCourseIds(
        new Set(
          (inv.data || [])
            .filter((x) => x?.course_id && x?.status !== "cancelled")
            .map((x) => x.course_id)
        )
      );
    } catch (e) {
      logError("No se pudieron cargar los datos del panel de cursos", e);
      toast.error("No se pudieron cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // CARGA de asistencias (Excel o captura): rellena solo lo vacío y suma las
  // observaciones. En un curso nuevo los alumnos se guardan al crearlo.
  const [pendingAttendance, setPendingAttendance] = useState(null);
  const normText = (s) =>
    String(s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ").trim().toLowerCase();
  const handleAttendanceLoaded = async (data, fileName) => {
    setForm((prev) => {
      const obs = (data.observations || "").trim();
      const notes = prev.description || "";
      return {
        ...prev,
        contact_name: prev.contact_name || data.contact_name || "",
        contact_phone: prev.contact_phone || data.contact_phone || "",
        contact_email: prev.contact_email || data.contact_email || "",
        description:
          obs && !normText(notes).includes(normText(obs))
            ? `${notes.trim() ? `${notes.trimEnd()}\n` : ""}Observaciones: ${obs}`
            : notes,
      };
    });
    const n = data.students?.length || 0;
    if (!editingId) {
      setPendingAttendance({ data, fileName, n });
      toast.success(`${n} alumnos leídos: se inscribirán al crear el curso.`);
      return;
    }
    try {
      const r = await saveAttendanceData(editingId, data);
      const extras = [
        r.contact_updated && "contacto",
        r.notes_updated && "observaciones",
      ].filter(Boolean);
      toast.success(
        (r.added
          ? `${r.added} alumnos nuevos inscritos (${r.total} en total).`
          : `Todos los alumnos ya estaban inscritos (${r.total} en total).`)
          + (extras.length ? ` Añadido: ${extras.join(" y ")}.` : "")
      );
      fetchAll();
      fetchProgress();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron inscribir los alumnos.");
    }
  };

  const saveBsafeContact = async () => {
    if (!bsafeForm.name.trim()) {
      toast.error("Pon al menos el nombre.");
      return;
    }
    setBsafeSaving(true);
    try {
      const payload = {
        name: bsafeForm.name.trim().toUpperCase(),
        surname: bsafeForm.surname.trim().toUpperCase(),
        email: bsafeForm.email.trim(),
        phone: bsafeForm.phone.trim(),
      };
      if (bsafeForm.id) {
        await adminApi.put(`/bsafe-contacts/${bsafeForm.id}`, payload);
      } else {
        await adminApi.post("/bsafe-contacts", payload);
      }
      const res = await adminApi.get("/bsafe-contacts");
      setBsafeContacts(res.data || []);
      setBsafeForm({ id: null, name: "", surname: "", email: "", phone: "" });
      toast.success("Contacto de BSAFE guardado.");
    } catch (e) {
      logError("No se pudo guardar el contacto de BSAFE", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    } finally {
      setBsafeSaving(false);
    }
  };

  const deleteBsafeContact = async (id) => {
    try {
      await adminApi.delete(`/bsafe-contacts/${id}`);
      setBsafeContacts((list) => list.filter((c) => c.id !== id));
      toast.success("Contacto eliminado.");
    } catch (e) {
      logError("No se pudo eliminar el contacto de BSAFE", e);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  // Filtrado + orden del listado. Primero se acota por fecha (actuales vs
  // pasados) y después se aplican los filtros combinables y la ordenación.
  const dateScopedCourses = useMemo(() => {
    const todayMadrid = new Date().toLocaleString("sv-SE", {
      timeZone: "Europe/Madrid",
    }).slice(0, 10); // YYYY-MM-DD
    return courses.filter((c) => {
      const d = c.date1 || "";
      if (showPast) return d && d < todayMadrid;
      // "En curso o futuros": sin fecha → se considera vigente
      return !d || d >= todayMadrid;
    });
  }, [courses, showPast]);

  const clientOptions = useMemo(() => {
    const set = new Set(
      dateScopedCourses
        .map((c) => String(c.client_name || c.name || "").trim())
        .filter(Boolean)
    );
    return [...set].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
  }, [dateScopedCourses]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const setFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value === "all" ? "" : value }));

  const filteredCourses = useMemo(() => {
    const arr = dateScopedCourses.filter((c) => {
      if (
        filters.client &&
        String(c.client_name || c.name || "").trim() !== filters.client
      ) return false;
      if (filters.month) {
        const months = [c.date1, c.date2]
          .filter(Boolean)
          .map((d) => String(d).slice(5, 7));
        if (!months.includes(filters.month)) return false;
      }
      if (filters.instructor && (c.instructor_id || "") !== filters.instructor)
        return false;
      if (filters.region && (c.region_id || "") !== filters.region) return false;
      if (filters.type && (c.course_type || "") !== filters.type) return false;
      if (filters.status === "active" && c.active === false) return false;
      if (filters.status === "inactive" && c.active !== false) return false;
      if (filters.fundae === "yes" && !c.fundae) return false;
      if (filters.fundae === "no" && c.fundae) return false;
      if (filters.billed === "yes" && !invoicedCourseIds.has(c.id)) return false;
      if (filters.billed === "no" && invoicedCourseIds.has(c.id)) return false;
      return true;
    });

    const byDate = (a, b) =>
      String(a.date1 || "9999-99-99").localeCompare(String(b.date1 || "9999-99-99"));
    const byName = (a, b) =>
      String(a.client_name || a.name || "").trim().localeCompare(
        String(b.client_name || b.name || "").trim(),
        "es",
        { sensitivity: "base" }
      );

    if (sortMode === "date_desc") arr.sort((a, b) => byDate(b, a));
    else if (sortMode === "alpha_asc") arr.sort(byName);
    else if (sortMode === "alpha_desc") arr.sort((a, b) => byName(b, a));
    else arr.sort(byDate);

    return arr;
  }, [dateScopedCourses, filters, invoicedCourseIds, sortMode]);

  // Firmas y exámenes de los cursos en pantalla: se refrescan solos
  const [progress, setProgress] = useState({});
  const progressIds = useMemo(
    () => filteredCourses.map((c) => c.id).join(","),
    [filteredCourses]
  );
  const fetchProgress = useCallback(async () => {
    if (!progressIds) return;
    try {
      const res = await adminApi.get("/admin/courses/progress", {
        params: { ids: progressIds },
      });
      setProgress((prev) => ({ ...prev, ...(res.data || {}) }));
    } catch (e) {
      logWarn("No se pudo leer el avance de los cursos", e);
    }
  }, [progressIds]);
  useEffect(() => {
    fetchProgress();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") fetchProgress();
    }, 15000);
    return () => clearInterval(t);
  }, [fetchProgress]);

  const selectedRegion = useMemo(
    () => regions.find((r) => r.id === form.region_id),
    [regions, form.region_id]
  );

  const computedHours = useMemo(() => {
    if (!selectedRegion) return null;
    return form.course_type === "reciclaje"
      ? selectedRegion.hours_refresher
      : selectedRegion.hours_initial;
  }, [selectedRegion, form.course_type]);

  const computedMaxStudents = selectedRegion?.max_students ?? null;

  // Los cursos de 3 días (12 h · 3 × 4 h) sólo existen en MURCIA y sólo
  // para formación INICIAL.
  const isMurciaRegion = useMemo(
    () =>
      String(selectedRegion?.name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .includes("MURCIA"),
    [selectedRegion]
  );
  const allowThreeDays = isMurciaRegion && form.course_type === "inicial";

  useEffect(() => {
    if (parseInt(form.days, 10) === 3 && !allowThreeDays) {
      setForm((f) => ({ ...f, days: 2 }));
    }
  }, [allowThreeDays, form.days]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPendingAttendance(null);
    setDialogOpen(true);
  };

  const openEdit = (course) => {
    setEditingId(course.id);
    setPendingAttendance(null);
    // Align fallbacks for legacy courses (older 10/70 defaults) to new 15/50
    // Determine if the course was shared with the other group:
    // - If admin is desfiemer and course is also visible to issefe → for_other_group=true
    // - If admin is issefe and course is also visible to desfiemer → for_other_group=true
    const shared = adminGroup === "issefe"
      ? !!course.visible_to_desfiemer
      : !!course.visible_to_issefe;
    setForm({
      client_name: course.client_name || "",
      address: course.address || "",
      city: course.city || "",
      region_id: course.region_id || undefined,
      course_type: course.course_type || "inicial",
      days: course.days || 1,
      date1: course.date1 || "",
      date2: course.date2 || "",
      date3: course.date3 || "",
      time_start_day1: course.time_start_day1 || "",
      time_end_day1: course.time_end_day1 || "",
      time_start_day2: course.time_start_day2 || "",
      time_end_day2: course.time_end_day2 || "",
      time_start_day3: course.time_start_day3 || "",
      time_end_day3: course.time_end_day3 || "",
      instructor_id: course.instructor_id || undefined,
      fundae: !!course.fundae,
      defibrillator: course.defibrillator || "",
      pernocta: !!course.pernocta,
      for_other_group: shared,
      contact_name: course.contact_name || "",
      contact_phone: course.contact_phone || "",
      contact_email: course.contact_email || "",
      bsafe_contact_id: course.bsafe_contact_id || "",
      description: course.description || "",
      active: course.active,
      exam_question_count: course.exam_question_count ?? 15,
      exam_pass_score: course.exam_pass_score ?? 50,
      ppaa_course_name: course.ppaa_course_name || "Primeros Auxilios",
      ppaa_syllabus: (course.ppaa_syllabus || []).join("\n"),
    });
    setDialogOpen(true);
  };

  // ---------------- Auto-detección de CCAA (Nominatim) ----------------
  // Cuando el usuario introduce dirección + ciudad y la CCAA está vacía,
  // interrogamos a Nominatim y prellenamos `region_id` automáticamente. Nunca
  // sobreescribimos una CCAA ya elegida a mano — la idea es acelerar el
  // rellenado, no cambiar decisiones del gestor.
  const detectCcaa = useCallback(async ({ silent = false } = {}) => {
    const addr = (form.address || "").trim();
    if (!addr) {
      if (!silent) toast.error("Escribe primero la dirección de impartición.");
      return;
    }
    setCcaaAutoDetecting(true);
    try {
      const res = await adminApi.post("/admin/geocode/ccaa", {
        address: addr,
        city: "",
      });
      const data = res.data || {};
      if (data.ok && data.region_id) {
        setForm((prev) => ({ ...prev, region_id: data.region_id }));
        setCcaaAutoHint({
          type: "success",
          text: `CCAA detectada: ${data.region_name}`,
        });
        if (!silent) toast.success(`CCAA detectada: ${data.region_name}`);
      } else if (data.raw_state) {
        setCcaaAutoHint({
          type: "warn",
          text: `Nominatim devolvió "${data.raw_state}", pero no está entre las CCAA soportadas. Selecciona manualmente.`,
        });
      } else {
        setCcaaAutoHint({
          type: "warn",
          text: "No se ha podido detectar la CCAA. Selecciona manualmente.",
        });
      }
    } catch (e) {
      logError("Error detectando CCAA", e);
      if (!silent) toast.error("No se pudo detectar la CCAA.");
    } finally {
      setCcaaAutoDetecting(false);
    }
  }, [form.address]);

  // Auto-invocación con debounce cuando se cambia address/city y la CCAA
  // aún no está seleccionada. 800 ms es suficiente para que el gestor
  // termine de escribir sin bombardear Nominatim.
  useEffect(() => {
    if (!dialogOpen) return;
    if (form.region_id) return; // ya elegida a mano o detectada
    const addr = (form.address || "").trim();
    if (!addr) return;
    if (addr.length < 4) return;
    const t = setTimeout(() => {
      detectCcaa({ silent: true });
    }, 800);
    return () => clearTimeout(t);
  }, [dialogOpen, form.address, form.region_id, detectCcaa]);

  // Reset del hint cuando cambia la CCAA a mano o se cierra el modal
  useEffect(() => {
    if (!dialogOpen) setCcaaAutoHint(null);
  }, [dialogOpen]);

  // ---------------- OCR de captura (Gemini Vision) ----------------
  const handleScreenshotLoad = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("El archivo debe ser una imagen (JPG/PNG/WEBP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La imagen es demasiado grande (máximo 8 MB).");
      return;
    }
    setParsingScreenshot(true);
    try {
      const dataUrl = await fileToBase64(file);
      const res = await adminApi.post(
        "/admin/courses/parse-screenshot",
        { image_base64: dataUrl }
      );
      const data = res.data || {};
      // Apply only non-null values, keep the rest as the user already had
      setForm((prev) => {
        const upd = { ...prev };
        if (data.client_name) upd.client_name = data.client_name.toUpperCase();
        if (data.address) upd.address = data.address.toUpperCase();
        if (data.date1) upd.date1 = data.date1;
        if (data.date2) upd.date2 = data.date2;
        if (data.time_start_day1) upd.time_start_day1 = data.time_start_day1;
        if (data.time_end_day1) upd.time_end_day1 = data.time_end_day1;
        if (data.time_start_day2) upd.time_start_day2 = data.time_start_day2;
        if (data.time_end_day2) upd.time_end_day2 = data.time_end_day2;
        if (data.days === 1 || data.days === 2) upd.days = data.days;
        if (data.contact_name) upd.contact_name = data.contact_name;
        if (data.contact_phone) upd.contact_phone = data.contact_phone;
        if (data.contact_email) upd.contact_email = data.contact_email;
        if (data.course_type) upd.course_type = data.course_type;
        if (typeof data.fundae === "boolean") upd.fundae = data.fundae;
        if (data.defibrillator) upd.defibrillator = data.defibrillator;
        // Quién nos ha pedido el curso: se busca entre los contactos de BSAFE
        const who = matchBsafeContact(data);
        if (who) upd.bsafe_contact_id = who;
        return upd;
      });
      const filledKeys = Object.entries(data)
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([k]) => k);
      if (filledKeys.length === 0) {
        toast.warning(
          "No se ha podido extraer ningún dato. Revisa la imagen y rellena manualmente."
        );
      } else {
        toast.success(
          `Datos extraídos (${filledKeys.length} campos). Revisa y guarda.`
        );
      }
    } catch (e) {
      logError("OCR error", e);
      toast.error(
        e.response?.data?.detail || "No se pudo procesar la imagen."
      );
    } finally {
      setParsingScreenshot(false);
    }
  }, [bsafeContacts]);

  // Empareja a quien nos pide el curso con la ficha de contacto de BSAFE.
  const matchBsafeContact = (data) => {
    const email = (data.requested_by_email || "").trim().toLowerCase();
    const name = (data.requested_by_name || "").trim().toLowerCase();
    if (!email && !name) return "";
    const found = bsafeContacts.find((c) => {
      const cEmail = (c.email || "").trim().toLowerCase();
      const cName = `${c.name || ""} ${c.surname || ""}`.trim().toLowerCase();
      return (email && cEmail === email) || (name && cName && cName === name);
    });
    return found?.id || "";
  };

  const handleSave = async ({ close = true } = {}) => {
    if (!form.client_name.trim()) {
      toast.error("El nombre del cliente es obligatorio.");
      return;
    }
    if (!form.address.trim()) {
      toast.error("La dirección de impartición es obligatoria.");
      return;
    }
    if (!form.region_id) {
      toast.error("Selecciona la comunidad autónoma.");
      return;
    }
    if (!form.date1) {
      toast.error("Indica la fecha del curso.");
      return;
    }
    if (parseInt(form.days, 10) >= 2 && !form.date2) {
      toast.error("Indica la segunda fecha del curso.");
      return;
    }
    if (parseInt(form.days, 10) === 3 && !form.date3) {
      toast.error("Indica la tercera fecha del curso.");
      return;
    }
    const qc = parseInt(form.exam_question_count, 10);
    const ps = parseInt(form.exam_pass_score, 10);
    if (!Number.isFinite(qc) || qc < 1) {
      toast.error("El nº de preguntas debe ser un entero ≥ 1.");
      return;
    }
    if (!Number.isFinite(ps) || ps < 0 || ps > 100) {
      toast.error("La nota mínima debe estar entre 0 y 100.");
      return;
    }
    const isPpaa = form.course_type === "ppaa";
    if (isPpaa && ppaaSyllabusItems(form.ppaa_syllabus).length === 0) {
      toast.error("Escribe el temario del curso de Primeros Auxilios.");
      return;
    }
    if (isPpaa && ppaaSyllabusLines(form.ppaa_syllabus) > PPAA_MAX_LINES) {
      toast.error("El temario no cabe en el certificado: acórtalo o quita algún punto.");
      return;
    }

    // Client-side hours validation: must meet region's minimum for the chosen type
    if (!isPpaa && selectedRegion && computedHours != null && totalScheduledHours != null) {
      if (totalScheduledHours + 0.01 < computedHours) {
        setHoursWarning({
          total: totalScheduledHours,
          required: computedHours,
          region: selectedRegion.name,
          type: form.course_type === "reciclaje" ? "reciclaje" : "inicial",
        });
        return;
      }
    }

    setSaving(true);
    try {
      const daysNum = parseInt(form.days, 10);
      const showSession2 =
        daysNum >= 2 ||
        (daysNum === 1 && form.course_type === "inicial");
      const payload = {
        client_name: form.client_name.trim(),
        address: form.address.trim(),
        region_id: form.region_id,
        course_type: form.course_type,
        days: daysNum,
        date1: form.date1,
        date2: daysNum >= 2 ? form.date2 : null,
        date3: daysNum >= 3 ? form.date3 : null,
        time_start_day1: form.time_start_day1 || null,
        time_end_day1: form.time_end_day1 || null,
        // Session 2 is used for: 2/3-day courses (día 2) OR 1-day initial
        // courses split in two sessions.
        time_start_day2: showSession2 ? form.time_start_day2 || null : null,
        time_end_day2: showSession2 ? form.time_end_day2 || null : null,
        // Día 3 — sólo cursos INICIAL de 3 días (MURCIA)
        time_start_day3: daysNum >= 3 ? form.time_start_day3 || null : null,
        time_end_day3: daysNum >= 3 ? form.time_end_day3 || null : null,
        instructor_id: form.instructor_id || null,
        fundae: !!form.fundae,
        defibrillator: form.defibrillator || "",
        pernocta: !!form.pernocta,
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_email: form.contact_email.trim(),
        bsafe_contact_id: form.bsafe_contact_id || null,
        description: form.description.trim(),
        active: form.active,
        exam_question_count: qc,
        exam_pass_score: ps,
        ppaa_course_name: isPpaa ? form.ppaa_course_name.trim() || "Primeros Auxilios" : null,
        ppaa_syllabus: isPpaa ? ppaaSyllabusItems(form.ppaa_syllabus) : [],
      };
      if (editingId) {
        await adminApi.put(`/courses/${editingId}`, payload);
        toast.success("Curso actualizado.");
      } else {
        const created = await adminApi.post(`/courses`, payload);
        toast.success("Curso creado.");
        if (pendingAttendance && created.data?.id) {
          try {
            const r = await saveAttendanceData(created.data.id, pendingAttendance.data);
            toast.success(`${r.added} alumnos inscritos en el curso.`);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "El curso se creó, pero no se pudieron inscribir los alumnos.");
          }
          setPendingAttendance(null);
          fetchProgress();
        }
      }
      // Al editar se guarda sin cerrar: el gestor sigue en la ficha.
      if (close) setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al guardar el curso.";
      toast.error(typeof msg === "string" ? msg : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const markAgentChecked = async (courseId) => {
    try {
      await adminApi.post(`/courses/${courseId}/agent-checked`);
      setCourses((list) => list.map((c) => (c.id === courseId ? { ...c, agent_checked: true } : c)));
      toast.success("Curso marcado como comprobado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar como comprobado.");
    }
  };

  const handleToggleActive = async (course) => {
    try {
      const shared = adminGroup === "issefe"
        ? !!course.visible_to_desfiemer
        : !!course.visible_to_issefe;
      await adminApi.put(`/courses/${course.id}`, {
        client_name: course.client_name || course.name || "—",
        address: course.address || "",
        city: course.city || "",
        region_id: course.region_id || null,
        course_type: course.course_type || "inicial",
        days: course.days || 1,
        date1: course.date1 || null,
        date2: course.date2 || null,
        date3: course.date3 || null,
        time_start_day1: course.time_start_day1 || null,
        time_end_day1: course.time_end_day1 || null,
        time_start_day2: course.time_start_day2 || null,
        time_end_day2: course.time_end_day2 || null,
        time_start_day3: course.time_start_day3 || null,
        time_end_day3: course.time_end_day3 || null,
        instructor_id: course.instructor_id || null,
        fundae: !!course.fundae,
        defibrillator: course.defibrillator || "",
        for_other_group: shared,
        contact_name: course.contact_name || "",
        contact_phone: course.contact_phone || "",
        contact_email: course.contact_email || "",
        description: course.description || "",
        active: !course.active,
        exam_question_count: course.exam_question_count ?? 15,
        exam_pass_score: course.exam_pass_score ?? 50,
        ppaa_course_name: course.ppaa_course_name || null,
        ppaa_syllabus: course.ppaa_syllabus || [],
      });
      await fetchAll();
    } catch (e) {
      toast.error("No se pudo cambiar el estado.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/courses/${deleteTarget.id}`);
      toast.success("Curso eliminado.");
      setDeleteTarget(null);
      await fetchAll();
    } catch (e) {
      toast.error("No se pudo eliminar.");
    }
  };

  const confirmRegeneratePin = async () => {
    if (!pinRegenTarget) return;
    setPinRegenLoading(true);
    try {
      const res = await adminApi.post(
        `/courses/${pinRegenTarget.id}/regenerate-pin`
      );
      const newPin = res.data?.access_pin;
      setPinRegenResult({
        oldPin: pinRegenTarget.access_pin,
        newPin,
      });
      // Refresh the course list to reflect the new PIN in the row.
      await fetchAll();
      toast.success("PIN regenerado.");
    } catch (e) {
      logError("Error regenerando PIN", e);
      toast.error(
        e.response?.data?.detail || "No se pudo regenerar el PIN."
      );
    } finally {
      setPinRegenLoading(false);
    }
  };

  return (
    <section data-testid="courses-manager">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h2 className="heading-font text-2xl font-bold text-zinc-950">
            Cursos
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            {filteredCourses.length} {filteredCourses.length === 1 ? "curso" : "cursos"}
            {showPast ? " pasados" : " en curso o futuros"}
            {courses.length !== filteredCourses.length && ` · ${courses.length} en total`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={openCreate}
            className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
            data-testid="new-course-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo curso
          </Button>
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger
              className="h-11 w-[235px] border border-zinc-300 bg-white"
              data-testid="sort-select"
              title="Ordenar el listado"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ArrowUpDown className="h-4 w-4 text-zinc-500 shrink-0" />
                <SelectValue placeholder="Ordenar por…" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem
                  key={o.value}
                  value={o.value}
                  data-testid={`sort-option-${o.value}`}
                >
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-11 px-4 border border-zinc-300 hover:bg-zinc-50"
                data-testid="filters-button"
                title="Filtrar el listado"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2 text-zinc-500" />
                Filtro
                {activeFilterCount > 0 && (
                  <span
                    className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-zinc-950 px-1.5 text-[11px] font-bold text-white"
                    data-testid="active-filters-count"
                  >
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[min(92vw,560px)] p-5"
              data-testid="filters-panel"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">
                  Filtrar cursos
                </div>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilters(emptyFilters)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-zinc-950"
                    data-testid="clear-filters-button"
                  >
                    <X className="h-3.5 w-3.5" />
                    Limpiar
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: "client", label: "Cliente", options: clientOptions.map((v) => [v, v]) },
                  { key: "month", label: "Mes", options: MONTHS },
                  {
                    key: "instructor",
                    label: "Instructor",
                    options: instructors.map((i) => [
                      i.id,
                      `${i.name || ""} ${String(i.surname || "").trim().split(/\s+/)[0] || ""}`.trim(),
                    ]),
                  },
                  { key: "region", label: "CCAA", options: regions.map((r) => [r.id, r.name]) },
                  {
                    key: "type",
                    label: "Tipo de curso",
                    options: [["inicial", "Inicial"], ["reciclaje", "Reciclaje"], ["ppaa", "PPAA"]],
                  },
                  {
                    key: "status",
                    label: "Estado",
                    options: [["active", "Activo"], ["inactive", "Inactivo"]],
                  },
                  { key: "fundae", label: "FUNDAE", options: [["yes", "Sí"], ["no", "No"]] },
                  {
                    key: "billed",
                    label: "Facturación",
                    options: [["yes", "Facturado"], ["no", "Sin facturar"]],
                  },
                ].map(({ key, label, options }) => (
                  <div key={key}>
                    <label className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-1.5">
                      {label}
                    </label>
                    <Select
                      value={filters[key] || "all"}
                      onValueChange={(v) => setFilter(key, v)}
                    >
                      <SelectTrigger
                        className="h-10 border border-zinc-300"
                        data-testid={`filter-${key}-select`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="all">Todos</SelectItem>
                        {options
                          .filter(([v]) => !!v)
                          .map(([v, l]) => (
                            <SelectItem
                              key={v}
                              value={v}
                              data-testid={`filter-${key}-option-${v}`}
                            >
                              {l}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Button
            onClick={() =>
              setShowPast((v) => {
                const next = !v;
                // Orden por defecto: actuales ASC (el más próximo arriba),
                // pasados DESC (el más reciente arriba).
                setSortMode(next ? "date_desc" : "date_asc");
                return next;
              })
            }
            className={
              showPast
                ? "h-11 px-5 bg-red-600 hover:bg-red-700 text-white font-bold tracking-wider"
                : "h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold tracking-wider"
            }
            data-testid="toggle-past-courses"
            title={
              showPast
                ? "Estás viendo cursos PASADOS. Pulsa para ver los actuales."
                : "Estás viendo cursos ACTUALES. Pulsa para ver los pasados."
            }
          >
            {showPast ? "PASADOS" : "ACTUALES"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500">
          Cargando…
        </div>
      ) : courses.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <BookOpen className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Aún no hay cursos
          </h3>
          <p className="text-sm text-zinc-600 mt-2">
            Crea tu primer curso para que los alumnos puedan firmar y examinarse.
          </p>
          <Button
            onClick={openCreate}
            className="mt-6 h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
            data-testid="empty-create-course-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear primer curso
          </Button>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div
          className="bg-white border border-zinc-200 rounded-lg p-12 text-center"
          data-testid="courses-empty-after-filter"
        >
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <BookOpen className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="heading-font text-lg font-bold text-zinc-950">
            No hay cursos {showPast ? "pasados" : "en curso o futuros"} con estos filtros
          </h3>
          <p className="text-sm text-zinc-600 mt-2">
            Cambia los filtros o pulsa &laquo;{showPast ? "PASADOS" : "ACTUALES"}&raquo;
            para ver los {showPast ? "actuales" : "pasados"}.
          </p>
          {activeFilterCount > 0 && (
            <Button
              variant="outline"
              onClick={() => setFilters(emptyFilters)}
              className="mt-5 h-10 border border-zinc-300 hover:bg-zinc-50"
              data-testid="clear-filters-empty-button"
            >
              <X className="h-4 w-4 mr-2" />
              Limpiar filtros
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCourses.map((c) => {
            const agentPending = c.created_by_agent === "recepcionista" && !c.agent_checked;
            return (
            <div
              key={c.id}
              // Violeta: lo creó el recepcionista y falta comprobarlo.
              // Naranja: el curso todavía no tiene instructor asignado
              className={`rounded-lg p-5 transition-colors ${
                agentPending
                  ? "bg-violet-50 border-2 border-violet-400 hover:border-violet-500"
                  : c.instructor_id
                  ? "bg-white border border-zinc-200 hover:border-zinc-400"
                  : "bg-orange-50 border-2 border-orange-400 hover:border-orange-500"
              }`}
              data-testid={`course-row-${c.id}`}
            >
              {agentPending && (
                <AgentCheckBar courseId={c.id} onChecked={markAgentChecked} />
              )}
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => openEdit(c)}
                  className="flex-1 min-w-0 text-left cursor-pointer group"
                  data-testid={`course-open-edit-${c.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-zinc-950 truncate group-hover:underline">
                      {c.client_name || c.name || "—"}
                    </div>
                    {c.access_pin && (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded bg-black text-white text-[12px] font-mono tracking-[0.22em] font-bold"
                        data-testid={`course-pin-${c.id}`}
                        title="PIN de acceso al curso"
                      >
                        {c.access_pin}
                      </span>
                    )}
                    {!c.active && (
                      <span className="px-2 py-0.5 text-[11px] uppercase tracking-wider bg-zinc-100 text-zinc-500 rounded border border-zinc-200">
                        Inactivo
                      </span>
                    )}
                    {instructorShortName(c, instructors) ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-sm text-zinc-600"
                        data-testid={`course-instructor-${c.id}`}
                        title="Instructor asignado"
                      >
                        <UserRound className="h-3.5 w-3.5 text-zinc-400" />
                        {instructorShortName(c, instructors)}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-orange-400 bg-white text-[11px] font-bold uppercase tracking-wider text-orange-700"
                        data-testid={`course-no-instructor-${c.id}`}
                        title="Este curso todavía no tiene instructor"
                      >
                        <UserRound className="h-3.5 w-3.5" />
                        Sin instructor
                      </span>
                    )}
                    {/* Traza de la última propuesta enviada por WhatsApp */}
                    {c.instructor_confirmed ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded border bg-emerald-50 text-emerald-800 border-emerald-200"
                        data-testid={`course-confirmed-${c.id}`}
                        title="Formación confirmada por el instructor"
                      >
                        Confirmado
                      </span>
                    ) : c.instructor_status && (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded border ${
                          c.instructor_status === "aceptada"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : c.instructor_status === "rechazada"
                              ? "bg-rose-50 text-rose-800 border-rose-200"
                              : "bg-amber-50 text-amber-800 border-amber-200"
                        }`}
                        data-testid={`course-proposal-status-${c.id}`}
                        title={`Propuesta a ${c.proposal_instructor_name || ""}`}
                      >
                        {c.proposal_instructor_name
                          ? `${c.proposal_instructor_name.split(" ")[0]} · `
                          : ""}
                        {c.instructor_status}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-zinc-600">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-zinc-400" />
                      {c.region_name || c.city || "—"}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded border ${
                        COURSE_TYPE_BADGE[c.course_type] || COURSE_TYPE_BADGE.inicial
                      }`}
                      data-testid={`course-type-${c.id}`}
                    >
                      {COURSE_TYPE_LABEL[c.course_type] || "Inicial"}
                    </span>
                    {["reciclaje", "ppaa"].includes(c.course_type) && (
                      <span
                        className="px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded border border-zinc-200 bg-zinc-50 text-zinc-600"
                        data-testid={`course-masks-${c.id}`}
                      >
                        Mascarillas:{" "}
                        {c.masks_decision === true ? "sí" : c.masks_decision === false ? "no" : "sin decidir"}
                      </span>
                    )}
                    {courseDatesLabel(c) && (
                      <span
                        className="text-zinc-500"
                        data-testid={`course-dates-${c.id}`}
                      >
                        {courseDatesLabel(c)}
                      </span>
                    )}
                    <span
                      className={
                        c.fundae
                          ? "px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded border bg-red-50 text-red-700 border-red-300"
                          : "px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded border bg-emerald-50 text-emerald-700 border-emerald-300"
                      }
                      data-testid={`course-fundae-${c.id}`}
                      title={
                        c.fundae
                          ? "Curso bonificado por FUNDAE"
                          : "Curso sin bonificación de FUNDAE"
                      }
                    >
                      {c.fundae ? "FUNDAE" : "NO FUNDAE"}
                    </span>
                  </div>
                </button>
                {c.bsafe_contact_name && (
                  <span
                    className="shrink-0 rounded-sm border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-sky-800"
                    data-testid={`course-bsafe-${c.id}`}
                    title="Contacto de BSAFE que pidió el curso"
                  >
                    BSAFE · {c.bsafe_contact_name}
                  </span>
                )}
                <div className="flex items-center gap-2 shrink-0">
                  {c.access_pin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPinRegenTarget(c);
                        setPinRegenResult(null);
                      }}
                      className="h-9 border border-zinc-300 hover:bg-zinc-50"
                      data-testid={`regen-pin-${c.id}`}
                      aria-label="Regenerar PIN"
                      title="Regenerar PIN"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(c);
                    }}
                    className="h-9 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                    data-testid={`delete-course-${c.id}`}
                    aria-label="Eliminar curso"
                    title="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Controles rápidos: Asistencia + Examen + Corrector */}
              <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center gap-2 flex-wrap">
                {(() => {
                  const attOpen = c.attendance_open !== false;
                  const examOpen = !!c.exam_unlocked;
                  const isAttToggling =
                    toggling?.id === c.id && toggling.kind === "attendance";
                  const isExamToggling =
                    toggling?.id === c.id && toggling.kind === "exam";
                  return (
                    <>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCourseField(c, "attendance");
                        }}
                        disabled={isAttToggling}
                        className={`h-9 px-3 rounded-md font-bold text-xs tracking-wide ${
                          attOpen
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "bg-zinc-200 hover:bg-zinc-300 text-zinc-800"
                        }`}
                        data-testid={`toggle-attendance-${c.id}`}
                      >
                        {isAttToggling ? (
                          <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        ) : attOpen ? (
                          <DoorOpen className="h-3.5 w-3.5 mr-2" />
                        ) : (
                          <DoorClosed className="h-3.5 w-3.5 mr-2" />
                        )}
                        {attOpen ? "CERRAR ASISTENCIA" : "ABRIR ASISTENCIA"}
                      </Button>
                      {c.course_type !== "ppaa" && (
                      <>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCourseField(c, "exam");
                        }}
                        disabled={isExamToggling}
                        className={`h-9 px-3 rounded-md font-bold text-xs tracking-wide ${
                          examOpen
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "bg-zinc-200 hover:bg-zinc-300 text-zinc-800"
                        }`}
                        data-testid={`toggle-exam-${c.id}`}
                      >
                        {isExamToggling ? (
                          <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        ) : examOpen ? (
                          <Lock className="h-3.5 w-3.5 mr-2" />
                        ) : (
                          <Unlock className="h-3.5 w-3.5 mr-2" />
                        )}
                        {examOpen ? "FIN EXAMEN" : "INICIO EXAMEN"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCorrector(c);
                        }}
                        className="h-9 px-3 border-zinc-300 hover:bg-zinc-50 text-xs"
                        data-testid={`open-corrector-${c.id}`}
                        title="Ver preguntas del examen con la respuesta correcta"
                      >
                        <BookOpenCheck className="h-3.5 w-3.5 mr-2" />
                        Corrector
                      </Button>
                      </>
                      )}
                      <CourseProgressChips course={c} progress={progress[c.id]} />
                    </>
                  );
                })()}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="course-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              {editingId ? "Editar curso" : "Nuevo curso"}
            </DialogTitle>
            <DialogDescription>
              Rellena los datos del curso. Las horas y el nº máximo de alumnos
              se calculan automáticamente según la comunidad y el tipo.
            </DialogDescription>
            {/* Botón CARGA — extrae datos de un correo/whatsapp con Gemini Vision */}
            <div className="pt-2 flex flex-wrap items-center gap-2">
              <label
                className={`inline-flex items-center gap-2 h-10 px-4 rounded-md border-2 border-dashed font-semibold cursor-pointer transition-colors text-sm ${
                  parsingScreenshot
                    ? "border-zinc-300 bg-zinc-100 text-zinc-500 cursor-wait"
                    : "border-zinc-900 text-zinc-900 hover:bg-zinc-100"
                }`}
                data-testid="course-screenshot-load-label"
                title="Sube una captura de un correo o WhatsApp y la IA pre-rellenará los campos del curso"
              >
                {parsingScreenshot ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Procesando imagen…
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-4 w-4" />
                    CARGA desde captura
                  </>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleScreenshotLoad}
                  disabled={parsingScreenshot}
                  data-testid="course-screenshot-load-input"
                />
              </label>
              <AttendanceLoaders onLoaded={handleAttendanceLoaded} />
              {(() => {
                const enrolled = editingId
                  ? courses.find((x) => x.id === editingId)?.pre_registered_students?.length || 0
                  : 0;
                if (!editingId && !pendingAttendance) return null;
                return (
                  <span className="text-xs text-zinc-600" data-testid="course-enrolled-count">
                    {editingId
                      ? `${enrolled} alumnos inscritos`
                      : `${pendingAttendance.n} alumnos de «${pendingAttendance.fileName}» listos para inscribir`}
                  </span>
                );
              })()}
            </div>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Cliente y dirección */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Nombre del cliente *
                </label>
                <Input
                  value={form.client_name}
                  onChange={(e) =>
                    setForm({ ...form, client_name: e.target.value.toUpperCase() })
                  }
                  placeholder="Ej. RENAULT ESPAÑA"
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="course-client-input"
                  autoFocus
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Dirección de impartición *
                </label>
                <Input
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value.toUpperCase() })
                  }
                  placeholder="CALLE, NÚMERO, CÓDIGO POSTAL, LOCALIDAD"
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="course-address-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2 flex items-center justify-between gap-2">
                  <span>Comunidad autónoma *</span>
                  <button
                    type="button"
                    onClick={() => detectCcaa({ silent: false })}
                    disabled={ccaaAutoDetecting || !form.address}
                    className="text-xs font-semibold text-zinc-500 hover:text-zinc-950 disabled:text-zinc-300 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    data-testid="course-region-autodetect-btn"
                    title="Detectar CCAA desde la dirección"
                  >
                    {ccaaAutoDetecting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <MapPin className="h-3 w-3" />
                    )}
                    {ccaaAutoDetecting ? "Detectando…" : "Auto-detectar"}
                  </button>
                </label>
                <Select
                  value={form.region_id || undefined}
                  onValueChange={(v) => setForm({ ...form, region_id: v })}
                >
                  <SelectTrigger
                    className="h-11 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
                    data-testid="course-region-select"
                  >
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ccaaAutoHint && (
                  <p
                    className={`mt-1 text-xs ${
                      ccaaAutoHint.type === "success"
                        ? "text-emerald-700"
                        : "text-amber-700"
                    }`}
                    data-testid="course-region-hint"
                  >
                    {ccaaAutoHint.text}
                  </p>
                )}
              </div>
            </div>

            {/* Tipo + días + fechas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Tipo
                </label>
                <RadioGroup
                  value={form.course_type}
                  onValueChange={(v) => setForm({ ...form, course_type: v })}
                  className="flex gap-2"
                >
                  {[
                    { v: "inicial", label: "Inicial" },
                    { v: "reciclaje", label: "Reciclaje" },
                    { v: "ppaa", label: "PPAA" },
                  ].map((o) => (
                    <label
                      key={o.v}
                      htmlFor={`type-${o.v}`}
                      className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-md border-2 cursor-pointer transition-colors ${
                        form.course_type === o.v
                          ? "border-zinc-950 bg-zinc-50 text-zinc-950 font-semibold"
                          : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"
                      }`}
                      data-testid={`course-type-${o.v}`}
                    >
                      <RadioGroupItem
                        value={o.v}
                        id={`type-${o.v}`}
                        className="sr-only"
                      />
                      {o.label}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Duración
                </label>
                <RadioGroup
                  value={String(form.days)}
                  onValueChange={(v) =>
                    setForm({ ...form, days: parseInt(v, 10) })
                  }
                  className="flex gap-2"
                >
                  {(allowThreeDays ? [1, 2, 3] : [1, 2]).map((n) => (
                    <label
                      key={n}
                      htmlFor={`days-${n}`}
                      className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-md border-2 cursor-pointer transition-colors ${
                        form.days === n
                          ? "border-zinc-950 bg-zinc-50 text-zinc-950 font-semibold"
                          : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"
                      }`}
                      data-testid={`course-days-${n}`}
                    >
                      <RadioGroupItem
                        value={String(n)}
                        id={`days-${n}`}
                        className="sr-only"
                      />
                      {n} día{n > 1 ? "s" : ""}
                    </label>
                  ))}
                </RadioGroup>
                {allowThreeDays && (
                  <p className="mt-2 text-xs text-zinc-500" data-testid="murcia-3days-hint">
                    Murcia · formación inicial: 3 días de 4 h (12 h en total).
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Fecha {form.days >= 2 ? "(día 1)" : ""} *
                </label>
                <Input
                  type="date"
                  value={form.date1}
                  onChange={(e) =>
                    setForm({ ...form, date1: e.target.value })
                  }
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="course-date1-input"
                />
              </div>
              {form.days >= 2 && (
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Fecha (día 2) *
                  </label>
                  <Input
                    type="date"
                    value={form.date2}
                    onChange={(e) =>
                      setForm({ ...form, date2: e.target.value })
                    }
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-date2-input"
                  />
                </div>
              )}
              {form.days >= 3 && (
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Fecha (día 3) *
                  </label>
                  <Input
                    type="date"
                    value={form.date3}
                    onChange={(e) =>
                      setForm({ ...form, date3: e.target.value })
                    }
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-date3-input"
                  />
                </div>
              )}
            </div>

            {/* Horario por sesión.
                Un curso inicial de 1 día tiene 2 sesiones (mañana + tarde),
                así que mostramos también el segundo par cuando course_type=inicial */}
            {(() => {
              const showSession2 =
                form.days >= 2 ||
                (form.days === 1 && form.course_type === "inicial");
              const labels =
                form.days >= 2
                  ? { s1: "Inicio día 1", e1: "Fin día 1", s2: "Inicio día 2", e2: "Fin día 2" }
                  : { s1: "Inicio sesión 1", e1: "Fin sesión 1", s2: "Inicio sesión 2", e2: "Fin sesión 2" };
              return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  {labels.s1}
                </label>
                <Input
                  type="time"
                  value={form.time_start_day1}
                  onChange={(e) =>
                    setForm({ ...form, time_start_day1: e.target.value })
                  }
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="course-time-start-day1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  {labels.e1}
                </label>
                <Input
                  type="time"
                  value={form.time_end_day1}
                  onChange={(e) =>
                    setForm({ ...form, time_end_day1: e.target.value })
                  }
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="course-time-end-day1"
                />
              </div>
              {showSession2 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      {labels.s2}
                    </label>
                    <Input
                      type="time"
                      value={form.time_start_day2}
                      onChange={(e) =>
                        setForm({ ...form, time_start_day2: e.target.value })
                      }
                      className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                      data-testid="course-time-start-day2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      {labels.e2}
                    </label>
                    <Input
                      type="time"
                      value={form.time_end_day2}
                      onChange={(e) =>
                        setForm({ ...form, time_end_day2: e.target.value })
                      }
                      className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                      data-testid="course-time-end-day2"
                    />
                  </div>
                </>
              )}
              {form.days >= 3 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Inicio día 3
                    </label>
                    <Input
                      type="time"
                      value={form.time_start_day3}
                      onChange={(e) =>
                        setForm({ ...form, time_start_day3: e.target.value })
                      }
                      className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                      data-testid="course-time-start-day3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Fin día 3
                    </label>
                    <Input
                      type="time"
                      value={form.time_end_day3}
                      onChange={(e) =>
                        setForm({ ...form, time_end_day3: e.target.value })
                      }
                      className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                      data-testid="course-time-end-day3"
                    />
                  </div>
                </>
              )}
            </div>
              );
            })()}

            {sessionWarning && (
              <div
                className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
                data-testid="session-warning"
              >
                <span className="text-amber-600 mt-0.5">⚠</span>
                <div>
                  <span className="font-semibold">Revisa el horario.</span>{" "}
                  {sessionWarning}
                </div>
              </div>
            )}

            {form.course_type === "ppaa" && (
              <PpaaCourseFields form={form} setForm={setForm} hours={totalScheduledHours} />
            )}

            {/* Instructor + Fundae */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Instructor
                </label>
                <Select
                  value={form.instructor_id || "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, instructor_id: v === "none" ? "" : v })
                  }
                >
                  <SelectTrigger
                    className="h-11 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
                    data-testid="course-instructor-select"
                  >
                    <SelectValue placeholder="Sin instructor asignado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin instructor asignado</SelectItem>
                    {instructors.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name} {i.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-zinc-500">
                  Puedes dejarlo sin asignar y buscarlo luego desde el panel de
                  propuestas por WhatsApp.
                </p>
              </div>
              <div className="flex items-end">
                <label
                  className={`w-full inline-flex items-center justify-between gap-3 h-11 px-4 rounded-md border-2 cursor-pointer transition-colors ${
                    form.fundae
                      ? "border-zinc-950 bg-zinc-50"
                      : "border-zinc-300"
                  }`}
                >
                  <span className="text-sm font-medium text-zinc-800">
                    Bonificado por Fundae
                  </span>
                  <Switch
                    checked={form.fundae}
                    onCheckedChange={(v) => setForm({ ...form, fundae: v })}
                    data-testid="course-fundae-switch"
                  />
                </label>
              </div>
            </div>

            {/* Desfibrilador + Pernocta (solo cursos de 2 dias) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Desfibrilador
                </label>
                <Select
                  value={form.defibrillator || undefined}
                  onValueChange={(v) =>
                    setForm({ ...form, defibrillator: v === "__none__" ? "" : v })
                  }
                >
                  <SelectTrigger
                    className="h-11 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
                    data-testid="course-defibrillator-select"
                  >
                    <SelectValue placeholder="Sin especificar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin especificar</SelectItem>
                    {DEFIBRILLATOR_BRANDS.map((b) => (
                      <SelectItem
                        key={b}
                        value={b}
                        data-testid={`defibrillator-option-${b}`}
                      >
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {parseInt(form.days, 10) >= 2 && (
                <div className="md:col-span-2 flex items-end">
                  <label
                    className={`w-full inline-flex items-center justify-between gap-3 h-11 px-4 rounded-md border-2 cursor-pointer transition-colors ${
                      form.pernocta
                        ? "border-zinc-950 bg-zinc-50"
                        : "border-zinc-300"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-800">
                      <Hotel className="h-4 w-4 text-zinc-500" />
                      Pernocta del instructor{" "}
                      <span className="text-zinc-500 font-normal">
                        (km × 2; sin pernocta km × 4)
                      </span>
                    </span>
                    <Switch
                      checked={form.pernocta}
                      onCheckedChange={(v) => setForm({ ...form, pernocta: v })}
                      data-testid="course-pernocta-switch"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Asignación de instructor: ranking por cercanía + WhatsApp */}
            {editingId && (
              <InstructorAssignment
                courseId={editingId}
                onAssigned={() => fetchAll()}
              />
            )}

            {/* BSAFE: quién nos ha pedido el curso */}
            <div className="pt-2 border-t border-zinc-200">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                  BSAFE · quién nos ha pedido el curso
                </div>
                <button
                  type="button"
                  onClick={() => setBsafeDialogOpen(true)}
                  className="text-xs font-semibold text-zinc-500 hover:text-zinc-950"
                  data-testid="course-bsafe-manage-btn"
                >
                  Gestionar contactos
                </button>
              </div>
              <Select
                value={form.bsafe_contact_id || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, bsafe_contact_id: v === "none" ? "" : v })
                }
              >
                <SelectTrigger
                  className="h-11 border-2 border-zinc-300 focus:border-zinc-900 focus:ring-0"
                  data-testid="course-bsafe-select"
                >
                  <SelectValue placeholder="Sin contacto de BSAFE" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin contacto de BSAFE</SelectItem>
                  {bsafeContacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {`${c.name} ${c.surname}`.trim()}
                      {c.email ? ` · ${c.email}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Persona de contacto */}
            <div className="pt-2 border-t border-zinc-200">
              <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-3">
                Persona de contacto (opcional)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Nombre
                  </label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) =>
                      setForm({ ...form, contact_name: e.target.value.toUpperCase() })
                    }
                    placeholder="NOMBRE Y APELLIDOS"
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Teléfono
                  </label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) =>
                      setForm({ ...form, contact_phone: e.target.value })
                    }
                    placeholder="600000000"
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Correo electrónico
                  </label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) =>
                      setForm({ ...form, contact_email: e.target.value })
                    }
                    placeholder="contacto@empresa.com"
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-contact-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Auto-calculated info */}
            {selectedRegion && form.course_type !== "ppaa" && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
                <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
                  Calculado automáticamente · {selectedRegion.name}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-zinc-500">Horas exigidas</div>
                    <div className="text-lg font-semibold text-zinc-950">
                      {computedHours != null ? `${computedHours} h` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Horario sumado</div>
                    <div
                      className={`text-lg font-semibold ${
                        totalScheduledHours != null &&
                        computedHours != null &&
                        totalScheduledHours + 0.01 < computedHours
                          ? "text-red-700"
                          : "text-zinc-950"
                      }`}
                      data-testid="course-scheduled-hours"
                    >
                      {totalScheduledHours != null
                        ? `${totalScheduledHours} h`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Máx. alumnos</div>
                    <div className="text-lg font-semibold text-zinc-950">
                      {computedMaxStudents != null
                        ? computedMaxStudents
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Examen + activo + descripcion */}
            {form.course_type !== "ppaa" && (
            <div className="pt-2 border-t border-zinc-200" data-testid="course-exam-section">
              <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-3">
                Examen
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Nº de preguntas
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={200}
                    value={form.exam_question_count}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        exam_question_count: e.target.value,
                      })
                    }
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-exam-count-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Nota mínima (%)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={form.exam_pass_score}
                    onChange={(e) =>
                      setForm({ ...form, exam_pass_score: e.target.value })
                    }
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                    data-testid="course-exam-pass-input"
                  />
                </div>
              </div>

              {/* Estado del banco de preguntas congelado para el curso */}
              {editingId && (
                <div
                  className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm flex items-start gap-3"
                  data-testid="exam-questions-status"
                >
                  <div className="flex-1 text-zinc-700">
                    {editingCourse?.exam_question_ids?.length > 0 ? (
                      <>
                        <span className="font-semibold text-zinc-950">
                          Set congelado:{" "}
                          {editingCourse.exam_question_ids.length} preguntas
                        </span>{" "}
                        ya fijadas para este curso. Todos los alumnos
                        (y sus reintentos) reciben las mismas preguntas en
                        distinto orden.
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-zinc-950">
                          Sin set congelado.
                        </span>{" "}
                        Las preguntas se sortearán cuando el primer alumno
                        inicie el examen, y se reutilizarán para los demás.
                      </>
                    )}
                  </div>
                  {editingCourse?.exam_question_ids?.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleResetExamQuestions}
                      disabled={resettingQuestions}
                      className="h-8 border border-zinc-300 hover:bg-white shrink-0"
                      data-testid="reset-exam-questions-button"
                    >
                      {resettingQuestions ? "Reseteando…" : "Resetear"}
                    </Button>
                  )}
                </div>
              )}
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Notas internas (opcional)
              </label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                rows={2}
                className="border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="course-description-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              className="border border-zinc-300"
              data-testid="course-cancel-button"
            >
              {editingId ? "SALIR" : "Cancelar"}
            </Button>
            <Button
              onClick={() => handleSave({ close: !editingId })}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="course-save-button"
            >
              {saving ? "Guardando…" : editingId ? "GUARDAR" : createLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-course-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar curso?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará{" "}
              <span className="font-semibold text-zinc-950">
                {deleteTarget?.name || deleteTarget?.client_name}
              </span>
              . Las asistencias asociadas se mantendrán como histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-course-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-course-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerar PIN */}
      <AlertDialog
        open={!!pinRegenTarget}
        onOpenChange={(o) => {
          if (!o) {
            setPinRegenTarget(null);
            setPinRegenResult(null);
          }
        }}
      >
        <AlertDialogContent data-testid="regen-pin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pinRegenResult ? "PIN regenerado" : "¿Regenerar PIN del curso?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {!pinRegenResult ? (
                  <>
                    <div>
                      Esto invalidará el PIN actual de{" "}
                      <span className="font-semibold text-zinc-950">
                        {pinRegenTarget?.client_name ||
                          pinRegenTarget?.name ||
                          "este curso"}
                      </span>{" "}
                      y generará uno nuevo. Los alumnos deberán usar el
                      nuevo código para acceder.
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                        PIN actual
                      </span>
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded bg-black text-white text-[13px] font-mono tracking-[0.22em] font-bold"
                        data-testid="regen-pin-current"
                      >
                        {pinRegenTarget?.access_pin || "—"}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      El PIN del curso se ha actualizado. Comunícaselo al
                      instructor para que pueda dárselo a los alumnos.
                    </div>
                    <div className="flex items-center gap-2 text-zinc-500 line-through">
                      <span className="text-xs uppercase tracking-[0.14em]">
                        Anterior
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded bg-zinc-200 text-zinc-500 text-[13px] font-mono tracking-[0.22em] font-bold">
                        {pinRegenResult.oldPin || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                        Nuevo
                      </span>
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded bg-emerald-600 text-white text-[15px] font-mono tracking-[0.22em] font-bold"
                        data-testid="regen-pin-new"
                      >
                        {pinRegenResult.newPin}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {!pinRegenResult ? (
              <>
                <AlertDialogCancel
                  disabled={pinRegenLoading}
                  data-testid="regen-pin-cancel"
                >
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    confirmRegeneratePin();
                  }}
                  disabled={pinRegenLoading}
                  className="bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid="regen-pin-confirm"
                >
                  {pinRegenLoading ? "Generando…" : "Regenerar PIN"}
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction
                onClick={() => {
                  setPinRegenTarget(null);
                  setPinRegenResult(null);
                }}
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
                data-testid="regen-pin-close"
              >
                Cerrar
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hours validation warning */}
      <AlertDialog
        open={!!hoursWarning}
        onOpenChange={(o) => !o && setHoursWarning(null)}
      >
        <AlertDialogContent data-testid="hours-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Horarios insuficientes</AlertDialogTitle>
            <AlertDialogDescription>
              {hoursWarning && (
                <>
                  Los horarios suman{" "}
                  <span className="font-bold text-red-700">
                    {hoursWarning.total} h
                  </span>{" "}
                  pero{" "}
                  <span className="font-bold">{hoursWarning.region}</span>{" "}
                  exige un mínimo de{" "}
                  <span className="font-bold">{hoursWarning.required} h</span>{" "}
                  para un curso{" "}
                  <span className="font-bold">{hoursWarning.type}</span>. No
                  se ha podido guardar el curso. Revisa los horarios.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setHoursWarning(null)}
              className="bg-zinc-950 hover:bg-zinc-800"
              data-testid="hours-warning-close"
            >
              Cerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Students of a course modal */}
      <Dialog
        open={!!studentsModal}
        onOpenChange={(o) => !o && setStudentsModal(null)}
      >
        <DialogContent
          className="bg-white border border-zinc-200 max-w-4xl"
          data-testid="course-students-modal"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl font-bold text-zinc-950">
              Alumnos · {studentsModal?.client_name || studentsModal?.name || ""}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Listado de alumnos pre-cargados desde Excel y alumnos que ya han firmado en este curso.
            </DialogDescription>
            <div className="mt-1 flex items-center gap-3 text-sm text-zinc-600">
              <span data-testid="students-modal-active-count">
                <strong className="text-zinc-900">
                  {studentsModalData.attendances.length}
                </strong>{" "}
                firmadas
              </span>
              <span className="text-zinc-300">·</span>
              <span data-testid="students-modal-pre-count">
                <strong className="text-zinc-900">
                  {studentsModalData.pre.length}
                </strong>{" "}
                pre-cargados (Excel)
              </span>
            </div>
          </DialogHeader>

          {/* Upload Excel of pre-registered students */}
          <div className="border border-dashed border-zinc-300 rounded-md p-3 flex items-center justify-between gap-3 bg-zinc-50">
            <div className="text-sm text-zinc-700">
              <span className="font-medium">Carga masiva de alumnos:</span>{" "}
              sube un Excel con columnas{" "}
              <span className="font-mono text-xs">
                NOMBRE / APELLIDOS / DNI
              </span>{" "}
              y los alumnos verán sus datos auto-rellenados al introducir su
              DNI.
            </div>
            <label
              className={`h-10 inline-flex shrink-0 items-center justify-center gap-2 px-3 border border-zinc-300 rounded text-sm font-medium cursor-pointer bg-white hover:bg-zinc-50 ${
                studentsModal && uploadingFor === studentsModal.id
                  ? "opacity-60 pointer-events-none"
                  : ""
              }`}
              data-testid={
                studentsModal
                  ? `upload-students-modal-${studentsModal.id}`
                  : "upload-students-modal"
              }
            >
              <Upload className="h-3.5 w-3.5" />
              {studentsModal && uploadingFor === studentsModal.id
                ? "Subiendo…"
                : "Cargar Excel"}
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f || !studentsModal) return;
                  await handleUploadExcel(studentsModal, f);
                  try {
                    const sRes = await adminApi.get(
                      `/courses/${studentsModal.id}/students`
                    );
                    setStudentsModalData((d) => ({
                      ...d,
                      pre: sRes.data || [],
                    }));
                  } catch (refreshError) {
                    // The upload succeeded but the refresh failed;
                    // user can still see the new students after re-opening
                    // the modal. Log so it's debuggable.
                    logWarn(
                      "No se pudo refrescar la lista de alumnos tras la carga",
                      refreshError
                    );
                  }
                }}
              />
            </label>
          </div>

          {/* Pre-registered students table (the 4-column table the user asked for) */}
          <div className="mt-2">
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
              Alumnos cargados
            </div>
            <div
              className="border border-zinc-200 rounded-md overflow-hidden"
              data-testid="students-pre-table"
            >
              <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                <div className="col-span-4">Nombre</div>
                <div className="col-span-5">Apellidos</div>
                <div className="col-span-3">DNI</div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {studentsModalData.loading ? (
                  <div className="px-4 py-6 text-sm text-zinc-500">
                    Cargando…
                  </div>
                ) : studentsModalData.pre.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-zinc-500">
                    Aún no se ha cargado ningún Excel para este curso.
                  </div>
                ) : (
                  studentsModalData.pre.map((s, i) => (
                    <div
                      key={`${s.dni || s.name}-${i}`}
                      className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-3 px-4 py-2.5 border-b border-zinc-100 last:border-b-0 text-sm"
                      data-testid={`pre-student-row-${i}`}
                    >
                      <div className="md:col-span-4 font-medium text-zinc-900 truncate">
                        {s.name || "—"}
                      </div>
                      <div className="md:col-span-5 text-zinc-800 truncate">
                        {s.surname || "—"}
                      </div>
                      <div className="md:col-span-3 font-mono text-zinc-800">
                        {s.dni || "—"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Signed attendances (if any) */}
          {studentsModalData.attendances.length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
                Alumnos que han firmado
              </div>
              <div className="border border-zinc-200 rounded-md overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                  <div className="col-span-4">Nombre</div>
                  <div className="col-span-5">Apellidos</div>
                  <div className="col-span-3">DNI</div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {studentsModalData.attendances.map((a) => (
                    <div
                      key={a.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-3 px-4 py-2.5 border-b border-zinc-100 last:border-b-0 text-sm"
                      data-testid={`signed-student-row-${a.id}`}
                    >
                      <div className="md:col-span-4 font-medium text-zinc-900 truncate">
                        {a.name || "—"}
                      </div>
                      <div className="md:col-span-5 text-zinc-800 truncate">
                        {a.surname || "—"}
                      </div>
                      <div className="md:col-span-3 font-mono text-zinc-800">
                        {a.dni || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setStudentsModal(null)}
              data-testid="close-students-modal"
              className="h-11"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corrector del examen (gestor) */}
      <Dialog
        open={!!correctorCourse}
        onOpenChange={(open) => !open && setCorrectorCourse(null)}
      >
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="admin-corrector-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl flex items-center gap-2">
              <BookOpenCheck className="h-5 w-5" />
              Corrector del examen
            </DialogTitle>
            <DialogDescription>
              Preguntas del examen de{" "}
              <strong>{correctorCourse?.client_name || correctorCourse?.name}</strong>{" "}
              con la respuesta correcta marcada en verde.
            </DialogDescription>
          </DialogHeader>
          {correctorLoading ? (
            <div className="py-8 text-center text-zinc-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Cargando preguntas…
            </div>
          ) : correctorQuestions.length === 0 ? (
            <div className="py-8 text-center text-zinc-500">
              No hay preguntas cargadas para este curso.
            </div>
          ) : (
            <ol
              className="space-y-4 list-none"
              data-testid="admin-corrector-questions-list"
            >
              {correctorQuestions.map((q, idx) => (
                <li
                  key={q.id}
                  className="border border-zinc-200 rounded-md p-4 bg-white"
                  data-testid={`admin-corrector-q-${q.id}`}
                >
                  <div className="font-semibold text-zinc-900 mb-3">
                    <span className="text-zinc-500 font-mono mr-2">
                      {idx + 1}.
                    </span>
                    {q.text || q.question || "—"}
                  </div>
                  <ul className="space-y-1.5 list-none">
                    {(q.options || []).map((opt, i) => {
                      const isCorrect = i === (q.correct_index ?? q.correct);
                      return (
                        <li
                          key={i}
                          className={`px-3 py-2 rounded border text-sm ${
                            isCorrect
                              ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-semibold"
                              : "bg-zinc-50 border-zinc-200 text-zinc-700"
                          }`}
                        >
                          <span className="font-mono mr-2">
                            {String.fromCharCode(65 + i)}.
                          </span>
                          {opt}
                          {isCorrect && (
                            <CheckCircle2 className="h-4 w-4 inline-block ml-2 -mt-0.5 text-emerald-600" />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ol>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCorrectorCourse(null)}
              data-testid="admin-corrector-close-btn"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contactos de BSAFE */}
      <Dialog open={bsafeDialogOpen} onOpenChange={setBsafeDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="bsafe-dialog">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              Contactos de BSAFE
            </DialogTitle>
            <DialogDescription>
              Las personas de BSAFE que nos encargan formaciones. Luego eliges
              en cada curso quién lo ha pedido.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {bsafeContacts.length === 0 && (
              <p className="text-sm text-zinc-500">Todavía no hay contactos.</p>
            )}
            {bsafeContacts.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-2 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2"
                data-testid={`bsafe-contact-${c.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-zinc-900">
                    {`${c.name} ${c.surname}`.trim()}
                  </p>
                  <p className="truncate text-xs text-zinc-500">
                    {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-2"
                    onClick={() =>
                      setBsafeForm({
                        id: c.id,
                        name: c.name || "",
                        surname: c.surname || "",
                        email: c.email || "",
                        phone: c.phone || "",
                      })
                    }
                    data-testid={`bsafe-edit-${c.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-2 text-red-600 hover:bg-red-50"
                    onClick={() => deleteBsafeContact(c.id)}
                    data-testid={`bsafe-delete-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2 border-t border-zinc-200 pt-3">
            <Input
              value={bsafeForm.name}
              onChange={(e) =>
                setBsafeForm({ ...bsafeForm, name: e.target.value.toUpperCase() })
              }
              placeholder="NOMBRE"
              className="h-10 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
              data-testid="bsafe-name-input"
            />
            <Input
              value={bsafeForm.surname}
              onChange={(e) =>
                setBsafeForm({ ...bsafeForm, surname: e.target.value.toUpperCase() })
              }
              placeholder="APELLIDOS"
              className="h-10 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
              data-testid="bsafe-surname-input"
            />
            <Input
              type="email"
              value={bsafeForm.email}
              onChange={(e) => setBsafeForm({ ...bsafeForm, email: e.target.value })}
              placeholder="correo@bsafe.es"
              className="h-10 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
              data-testid="bsafe-email-input"
            />
            <Input
              value={bsafeForm.phone}
              onChange={(e) => setBsafeForm({ ...bsafeForm, phone: e.target.value })}
              placeholder="600000000"
              className="h-10 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
              data-testid="bsafe-phone-input"
            />
          </div>

          <DialogFooter>
            {bsafeForm.id && (
              <Button
                variant="outline"
                onClick={() =>
                  setBsafeForm({ id: null, name: "", surname: "", email: "", phone: "" })
                }
              >
                Cancelar edición
              </Button>
            )}
            <Button
              onClick={saveBsafeContact}
              disabled={bsafeSaving}
              className="bg-zinc-950 hover:bg-zinc-800"
              data-testid="bsafe-save-button"
            >
              {bsafeSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : bsafeForm.id ? (
                "Guardar cambios"
              ) : (
                "Añadir contacto"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
